<template>
    <div class="ai-verifier">
        <DxSplitter orientation="horizontal">
            <DxItem :resizable="true" :size="500" :min-size="200">
                <template #default>
                    <div class="ai-verifier__image-section">
                        <div 
                            ref="imageContainerRef"
                            class="ai-verifier__image-container"
                            @wheel.prevent="onWheel"
                            @mousedown="onMouseDown"
                            @mousemove="onMouseMove"
                            @mouseup="onMouseUp"
                            @mouseleave="onMouseUp"
                        >
                            <div v-if="isLoadingImage" class="ai-verifier__loading">
                                <i class="dx-icon-refresh dx-icon-spin" style="font-size: 32px; opacity: 0.6;"></i>
                                <span>Loading preview...</span>
                            </div>
                            <img
                                v-else-if="localImageUrl"
                                :src="localImageUrl"
                                alt="Document preview"
                                class="ai-verifier__image"
                                :class="{ 'ai-verifier__image--dragging': isDragging }"
                                :style="imageTransformStyle"
                                @error="onImageError"
                                draggable="false"
                            />
                            <div v-else class="ai-verifier__no-image">
                                <i class="dx-icon-image" style="font-size: 48px; opacity: 0.4;"></i>
                                <span>No preview available</span>
                            </div>
                        </div>
                        <div class="ai-verifier__image-controls" v-if="localImageUrl && !isLoadingImage">
                            <button class="ai-verifier__control-btn" @click="zoomOut" title="Zoom out">
                                <i class="dx-icon-minus"></i>
                            </button>
                            <span class="ai-verifier__zoom-level">{{ Math.round(zoom * 100) }}%</span>
                            <button class="ai-verifier__control-btn" @click="zoomIn" title="Zoom in">
                                <i class="dx-icon-plus"></i>
                            </button>
                            <button class="ai-verifier__control-btn" @click="resetZoom" title="Reset view">
                                <i class="dx-icon-undo"></i>
                            </button>
                        </div>
                        <div class="ai-verifier__image-info">
                            <span class="ai-verifier__filename">{{ fileName }}</span>
                        </div>
                    </div>
                </template>
            </DxItem>
            <DxItem :resizable="true" :min-size="300">
                <template #default>
                    <div class="ai-verifier__content-section">
                        <div class="ai-verifier__label">AI Extracted Content</div>
                        <DxTextArea
                            v-model:value="localContent"
                            height="100%"
                            :auto-resize-enabled="false"
                            placeholder="AI extracted content will appear here..."
                            class="ai-verifier__textarea"
                        />
                        <div class="ai-verifier__hint">
                            Review and edit the AI-extracted content above. Click "Approve" to confirm the parsing is correct.
                        </div>
                    </div>
                </template>
            </DxItem>
        </DxSplitter>
    </div>
</template>

<script setup>
import { ref, computed, watch, inject, onBeforeUnmount, defineProps, defineEmits } from 'vue';
import DxTextArea from 'devextreme-vue/text-area';
import { DxSplitter, DxItem } from 'devextreme-vue/splitter';

const $http = inject('$http');

// Image zoom and pan state
const imageContainerRef = ref(null);
const zoom = ref(1);
const panX = ref(0);
const panY = ref(0);
const isDragging = ref(false);
const dragStart = ref({ x: 0, y: 0 });
const panStart = ref({ x: 0, y: 0 });

const MIN_ZOOM = 0.5;
const MAX_ZOOM = 5;
const ZOOM_STEP = 0.15;

const imageTransformStyle = computed(() => ({
    transform: `translate(${panX.value}px, ${panY.value}px) scale(${zoom.value})`,
    cursor: zoom.value > 1 ? (isDragging.value ? 'grabbing' : 'grab') : 'default',
}));

function onWheel(event) {
    const delta = event.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP;
    const newZoom = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, zoom.value + delta));
    
    // Zoom towards mouse position
    if (imageContainerRef.value) {
        const rect = imageContainerRef.value.getBoundingClientRect();
        const mouseX = event.clientX - rect.left - rect.width / 2;
        const mouseY = event.clientY - rect.top - rect.height / 2;
        
        const zoomRatio = newZoom / zoom.value;
        panX.value = mouseX - (mouseX - panX.value) * zoomRatio;
        panY.value = mouseY - (mouseY - panY.value) * zoomRatio;
    }
    
    zoom.value = newZoom;
    
    // Reset pan if zooming back to 1 or less
    if (zoom.value <= 1) {
        panX.value = 0;
        panY.value = 0;
    }
}

function onMouseDown(event) {
    if (zoom.value > 1 && event.button === 0) {
        isDragging.value = true;
        dragStart.value = { x: event.clientX, y: event.clientY };
        panStart.value = { x: panX.value, y: panY.value };
    }
}

function onMouseMove(event) {
    if (isDragging.value) {
        const dx = event.clientX - dragStart.value.x;
        const dy = event.clientY - dragStart.value.y;
        panX.value = panStart.value.x + dx;
        panY.value = panStart.value.y + dy;
    }
}

function onMouseUp() {
    isDragging.value = false;
}

function zoomIn() {
    zoom.value = Math.min(MAX_ZOOM, zoom.value + ZOOM_STEP * 2);
}

function zoomOut() {
    zoom.value = Math.max(MIN_ZOOM, zoom.value - ZOOM_STEP * 2);
    if (zoom.value <= 1) {
        panX.value = 0;
        panY.value = 0;
    }
}

function resetZoom() {
    zoom.value = 1;
    panX.value = 0;
    panY.value = 0;
}

const props = defineProps({
    fileId: {
        type: [Number, String],
        default: null,
    },
    fileName: {
        type: String,
        default: '',
    },
    imageUrl: {
        type: String,
        default: '',
    },
    content: {
        type: String,
        default: '',
    },
});

const emit = defineEmits(['update:content']);

const localContent = ref(props.content);
const localImageUrl = ref('');
const imageLoadError = ref(false);
const isLoadingImage = ref(false);

// Fetch image via $http and create a blob URL
async function fetchImage(url) {
    if (!url) {
        localImageUrl.value = '';
        return;
    }
    
    // Revoke previous blob URL if exists
    if (localImageUrl.value && localImageUrl.value.startsWith('blob:')) {
        URL.revokeObjectURL(localImageUrl.value);
    }
    
    try {
        isLoadingImage.value = true;
        const response = await $http.get(url, { responseType: 'blob' });
        const blob = response?.data;
        if (blob) {
            localImageUrl.value = URL.createObjectURL(blob);
        } else {
            localImageUrl.value = '';
        }
    } catch (error) {
        console.error('Failed to fetch image:', error);
        localImageUrl.value = '';
    } finally {
        isLoadingImage.value = false;
    }
}

watch(() => props.imageUrl, (newVal) => {
    resetZoom();
    fetchImage(newVal);
}, { immediate: true });

watch(() => props.content, (newVal) => {
    localContent.value = newVal;
});

watch(localContent, (newVal) => {
    emit('update:content', newVal);
});

function onImageError() {
    imageLoadError.value = true;
}

// Cleanup blob URL on unmount
onBeforeUnmount(() => {
    if (localImageUrl.value && localImageUrl.value.startsWith('blob:')) {
        URL.revokeObjectURL(localImageUrl.value);
    }
});
</script>

<style scoped>
.ai-verifier {
    height: calc(100% - 50px);
    min-height: 360px;
    box-sizing: border-box;
}

.ai-verifier :deep(.dx-splitter) {
    height: 100%;
}

.ai-verifier__image-section {
    display: flex;
    flex-direction: column;
    gap: 12px;
    height: 100%;
    padding: 8px;
    box-sizing: border-box;
}

.ai-verifier__image-container {
    width: 100%;
    flex: 1;
    min-height: 200px;
    border: 1px solid var(--dx-color-border, #e0e0e0);
    border-radius: 6px;
    overflow: hidden;
    background: #fafafa;
    display: flex;
    align-items: center;
    justify-content: center;
}

.ai-verifier__image {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
    transition: transform 0.1s ease-out;
    user-select: none;
}

.ai-verifier__image--dragging {
    transition: none;
}

.ai-verifier__image-controls {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 6px 0;
}

.ai-verifier__control-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border: 1px solid var(--dx-color-border, #e0e0e0);
    border-radius: 4px;
    background: var(--dx-color-bg, #fff);
    color: var(--dx-color-text, rgba(0, 0, 0, 0.87));
    cursor: pointer;
    transition: all 0.15s ease;
}

.ai-verifier__control-btn:hover {
    background: var(--dx-color-bg-hover, #f5f5f5);
    border-color: var(--dx-color-primary, #1976d2);
}

.ai-verifier__control-btn:active {
    transform: scale(0.95);
}

.ai-verifier__zoom-level {
    font-size: 12px;
    font-weight: 500;
    color: var(--dx-color-text-secondary, rgba(0, 0, 0, 0.6));
    min-width: 40px;
    text-align: center;
}

.ai-verifier__no-image {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    color: var(--dx-color-text-secondary, rgba(0, 0, 0, 0.54));
    font-size: 13px;
}

.ai-verifier__loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    color: var(--dx-color-text-secondary, rgba(0, 0, 0, 0.54));
    font-size: 13px;
}

.dx-icon-spin {
    animation: dx-icon-spin 1s linear infinite;
}

@keyframes dx-icon-spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
}

.ai-verifier__image-info {
    text-align: center;
}

.ai-verifier__filename {
    font-size: 13px;
    font-weight: 500;
    color: var(--dx-color-text, rgba(0, 0, 0, 0.87));
    word-break: break-word;
}

.ai-verifier__content-section {
    display: flex;
    flex-direction: column;
    gap: 8px;
    height: 100%;
    padding: 8px;
    box-sizing: border-box;
}

.ai-verifier__label {
    font-size: 14px;
    font-weight: 500;
    color: var(--dx-color-text, rgba(0, 0, 0, 0.87));
}

.ai-verifier__textarea {
    flex: 1;
    min-height: 0;
}

.ai-verifier__textarea :deep(.dx-texteditor-container) {
    height: 100%;
}

.ai-verifier__textarea :deep(.dx-texteditor-input-container) {
    height: 100%;
}

.ai-verifier__textarea :deep(textarea) {
    height: 100% !important;
}

.ai-verifier__hint {
    font-size: 12px;
    color: var(--dx-color-text-secondary, rgba(0, 0, 0, 0.6));
    line-height: 1.4;
}
</style>

